// src/components/ShopList.js

import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import axios from "axios";

const SHOP_LIST_API =
    "https://oppiesfunc.azurewebsites.net/api/getPendingShops?code=pGfodyNO5PkGcmb_H1XvCo7cknhQNa4FNMZzq_8o1suNAzFu-FFy9A==";

const APPROVE_SHOP_API =
    "https://oppiesfunc.azurewebsites.net/api/approveShop?code=NrDKuYLjllR0_uPRfF3Sd-pOTd_itfOfYf2DSN4AxGFOAzFu7RKe7g==";

const REJECT_SHOP_API =
    "https://oppiesfunc.azurewebsites.net/api/rejectShop?code=Jqzd2Ggm2gOjuj0i6sNQ03KGRuyeqmRquXnDorkV6ybZAzFu3za4OA==";

const CLAIM_LIST_API =
    "https://oppiesfunc.azurewebsites.net/api/getPendingShopClaims?code=8f000uiR-vLu7qHftzdn7gQFEdsuiQvSmSKKrb2hlY3pAzFufk86gQ==";

const APPROVE_CLAIM_API =
    "https://oppiesfunc.azurewebsites.net/api/approveShopClaim?code=9WmZRGDoS1AvBUcUEB7lqJ_2BGSMFVbPuhx24GHffCMRAzFujb4-vQ==";

const REJECT_CLAIM_API =
    "https://oppiesfunc.azurewebsites.net/api/rejectShopClaim?code=1rnMqRdAHa-OGbBNKRwa-Pl_cVR8iDCoHiEeeW2M9SCsAzFuMaiEGg==";

function formatDate(value) {
    if (!value) {
        return "Unknown";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString("en-NZ", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
}

function getApiError(error, fallback) {
    return (
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        fallback
    );
}

function StatusMessage({ status }) {
    if (!status.message) {
        return null;
    }

    return (
        <div
            className="card"
            style={{
                marginBottom: 14,
                borderColor: status.isError
                    ? "#ffcccc"
                    : "#d9f2e3",
                background: status.isError
                    ? "#fff6f6"
                    : "#f4fff8",
            }}
        >
            <div
                style={{
                    fontWeight: 800,
                    color: status.isError
                        ? "#a94442"
                        : "#1f5130",
                }}
            >
                {status.message}
            </div>
        </div>
    );
}

function RequestBadge({ type }) {
    const isClaim = type === "claim";

    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "5px 10px",
                borderRadius: 999,
                background: isClaim
                    ? "#6b5c3f"
                    : "#e8f5eb",
                color: isClaim
                    ? "#ffffff"
                    : "#245a35",
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: 0.6,
                whiteSpace: "nowrap",
            }}
        >
            {isClaim
                ? "OWNERSHIP CLAIM"
                : "NEW SHOP"}
        </span>
    );
}

function ShopList({
    requestFilter = "all",
    showRequestBadges = true,
}) {
    const [shops, setShops] = useState([]);
    const [claims, setClaims] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const [status, setStatus] = useState({
        message: "",
        isError: false,
    });

    const fetchRequests = useCallback(async () => {
        setLoading(true);

        try {
            const [shopsResult, claimsResult] =
                await Promise.allSettled([
                    axios.get(SHOP_LIST_API),
                    axios.get(CLAIM_LIST_API),
                ]);

            if (shopsResult.status === "fulfilled") {
                const shopData = shopsResult.value.data;

                setShops(
                    Array.isArray(shopData)
                        ? shopData
                        : Array.isArray(shopData?.items)
                            ? shopData.items
                            : []
                );
            } else {
                console.error(
                    "Failed to fetch pending shops:",
                    shopsResult.reason
                );

                setShops([]);
            }

            if (claimsResult.status === "fulfilled") {
                const claimData = claimsResult.value.data;

                setClaims(
                    Array.isArray(claimData)
                        ? claimData
                        : Array.isArray(claimData?.items)
                            ? claimData.items
                            : []
                );
            } else {
                console.error(
                    "Failed to fetch shop claims:",
                    claimsResult.reason
                );

                setClaims([]);
            }

            if (
                shopsResult.status === "rejected" &&
                claimsResult.status === "rejected"
            ) {
                throw new Error(
                    "Both request services failed."
                );
            }

            if (
                shopsResult.status === "rejected" ||
                claimsResult.status === "rejected"
            ) {
                setStatus({
                    message:
                        "Some shop requests could not be loaded. Please refresh and try again.",
                    isError: true,
                });
            } else {
                setStatus({
                    message: "",
                    isError: false,
                });
            }
        } catch (error) {
            console.error(
                "Failed to fetch shop requests:",
                error
            );

            setStatus({
                message:
                    "Failed to load shop requests.",
                isError: true,
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    const requests = useMemo(() => {
        const normalShopRequests = shops.map(
            (shop) => ({
                ...shop,

                requestType: "registration",

                requestKey:
                    `registration-${shop.id || shop.shopId}`,
            })
        );

        const ownershipClaims = claims.map(
            (claim) => ({
                ...claim,

                requestType: "claim",

                requestKey:
                    `claim-${claim.id}`,
            })
        );

        const combined = [
            ...ownershipClaims,
            ...normalShopRequests,
        ];

        if (requestFilter === "registration") {
            return combined.filter(
                (request) =>
                    request.requestType ===
                    "registration"
            );
        }

        if (requestFilter === "claim") {
            return combined.filter(
                (request) =>
                    request.requestType === "claim"
            );
        }

        return combined;
    }, [shops, claims, requestFilter]);

    const approveShop = async (shopId) => {
        if (
            !window.confirm(
                `Approve shop "${shopId}"?`
            )
        ) {
            return;
        }

        const requestId =
            `registration-${shopId}`;

        setBusyId(requestId);

        try {
            await axios.post(
                APPROVE_SHOP_API,
                {
                    shopId,
                }
            );

            setShops((previous) =>
                previous.filter(
                    (shop) =>
                        shop.shopId !== shopId
                )
            );

            setStatus({
                message:
                    `Approved shop "${shopId}".`,
                isError: false,
            });
        } catch (error) {
            console.error(
                "Shop approval failed:",
                error
            );

            setStatus({
                message: getApiError(
                    error,
                    "Failed to approve shop."
                ),
                isError: true,
            });
        } finally {
            setBusyId(null);
        }
    };

    const rejectShop = async (shopId) => {
        if (
            !window.confirm(
                `Reject shop "${shopId}"?`
            )
        ) {
            return;
        }

        const reason =
            window.prompt(
                "Optional: add a rejection reason:",
                ""
            ) || undefined;

        const requestId =
            `registration-${shopId}`;

        setBusyId(requestId);

        try {
            await axios.post(
                `${REJECT_SHOP_API}&mode=flag`,
                {
                    shopId,
                    reason,
                }
            );

            setShops((previous) =>
                previous.filter(
                    (shop) =>
                        shop.shopId !== shopId
                )
            );

            setStatus({
                message:
                    `Rejected shop "${shopId}".`,
                isError: false,
            });
        } catch (error) {
            console.error(
                "Shop rejection failed:",
                error
            );

            setStatus({
                message: getApiError(
                    error,
                    "Failed to reject shop."
                ),
                isError: true,
            });
        } finally {
            setBusyId(null);
        }
    };

    const approveClaim = async (claim) => {
        const claimId = claim.id;

        if (!claimId) {
            setStatus({
                message:
                    "This claim is missing its claim ID.",
                isError: true,
            });

            return;
        }

        const shopName =
            claim.shopName ||
            claim.shopId ||
            "this shop";

        if (
            !window.confirm(
                `Approve the ownership claim for "${shopName}"?\n\nThis will assign the shop to ${claim.claimantEmail}.`
            )
        ) {
            return;
        }

        const requestId =
            `claim-${claimId}`;

        setBusyId(requestId);

        try {
            await axios.post(
                APPROVE_CLAIM_API,
                {
                    claimId,

                    adminEmail:
                        "admin@oppies.nz",
                }
            );

            setClaims((previous) =>
                previous.filter(
                    (item) =>
                        item.id !== claimId
                )
            );

            setStatus({
                message:
                    `Approved the ownership claim for "${shopName}".`,
                isError: false,
            });
        } catch (error) {
            console.error(
                "Claim approval failed:",
                error
            );

            setStatus({
                message: getApiError(
                    error,
                    "Failed to approve ownership claim."
                ),
                isError: true,
            });
        } finally {
            setBusyId(null);
        }
    };

    const rejectClaim = async (claim) => {
        const claimId = claim.id;

        if (!claimId) {
            setStatus({
                message:
                    "This claim is missing its claim ID.",
                isError: true,
            });

            return;
        }

        const shopName =
            claim.shopName ||
            claim.shopId ||
            "this shop";

        if (
            !window.confirm(
                `Reject the ownership claim for "${shopName}"?`
            )
        ) {
            return;
        }

        const reason =
            window.prompt(
                "Optional: add a rejection reason:",
                ""
            ) ||
            "No rejection reason provided.";

        const requestId =
            `claim-${claimId}`;

        setBusyId(requestId);

        try {
            await axios.post(
                REJECT_CLAIM_API,
                {
                    claimId,

                    adminEmail:
                        "admin@oppies.nz",

                    reason,
                }
            );

            setClaims((previous) =>
                previous.filter(
                    (item) =>
                        item.id !== claimId
                )
            );

            setStatus({
                message:
                    `Rejected the ownership claim for "${shopName}".`,
                isError: false,
            });
        } catch (error) {
            console.error(
                "Claim rejection failed:",
                error
            );

            setStatus({
                message: getApiError(
                    error,
                    "Failed to reject ownership claim."
                ),
                isError: true,
            });
        } finally {
            setBusyId(null);
        }
    };

    if (loading) {
        return (
            <p style={{ textAlign: "center" }}>
                Loading shop requests...
            </p>
        );
    }

    return (
        <div style={{ width: "100%" }}>
            <StatusMessage status={status} />

            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginBottom: 14,
                }}
            >
                <button
                    type="button"
                    className="btn-secondary"
                    onClick={fetchRequests}
                    disabled={Boolean(busyId)}
                >
                    Refresh Requests
                </button>
            </div>

            {!requests.length ? (
                <div
                    className="card"
                    style={{
                        textAlign: "center",
                        padding: 28,
                        color: "#857969",
                    }}
                >
                    No shop requests awaiting approval.
                </div>
            ) : (
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns:
                            "repeat(auto-fit, minmax(360px, 1fr))",
                        gap: 16,
                    }}
                >
                    {requests.map((request) => {
                        const isClaim =
                            request.requestType ===
                            "claim";

                        const requestKey =
                            request.requestKey;

                        const disabled =
                            busyId === requestKey;

                        if (isClaim) {
                            const shopName =
                                request.shopName ||
                                request.shopId ||
                                "(Unnamed shop)";

                            return (
                                <div
                                    key={requestKey}
                                    className="card"
                                    style={{
                                        border:
                                            "2px solid #bfa97c",
                                        boxShadow:
                                            "0 6px 18px rgba(107, 92, 63, 0.12)",
                                    }}
                                >
                                    <div className="cardHeader">
                                        <div>
                                            {showRequestBadges && (
                                                <div
                                                    style={{
                                                        marginBottom: 9,
                                                    }}
                                                >
                                                    <RequestBadge type="claim" />
                                                </div>
                                            )}

                                            <h3
                                                className="cardTitle"
                                                style={{
                                                    marginBottom: 2,
                                                }}
                                            >
                                                {shopName}
                                            </h3>

                                            <div className="cardSubtitle">
                                                <strong>
                                                    Shop ID:
                                                </strong>{" "}
                                                {request.shopId ||
                                                    "Unknown"}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="cardBody">
                                        <p
                                            style={{
                                                margin: "6px 0",
                                            }}
                                        >
                                            <strong>
                                                Claimed by:
                                            </strong>{" "}
                                            {request.claimantEmail ||
                                                "Unknown"}
                                        </p>

                                        {request.claimantContact && (
                                            <p
                                                style={{
                                                    margin: "6px 0",
                                                }}
                                            >
                                                <strong>
                                                    Contact:
                                                </strong>{" "}
                                                {
                                                    request.claimantContact
                                                }
                                            </p>
                                        )}

                                        <p
                                            style={{
                                                margin: "6px 0",
                                            }}
                                        >
                                            <strong>
                                                Submitted:
                                            </strong>{" "}
                                            {formatDate(
                                                request.submittedAt
                                            )}
                                        </p>

                                        {request.addressFormatted && (
                                            <p
                                                style={{
                                                    margin: "6px 0",
                                                }}
                                            >
                                                <strong>
                                                    Address:
                                                </strong>{" "}
                                                {
                                                    request.addressFormatted
                                                }
                                            </p>
                                        )}

                                        <div
                                            style={{
                                                marginTop: 12,
                                                padding: 12,
                                                borderRadius: 8,
                                                background: "#f8f3e9",
                                                border:
                                                    "1px solid #e6dac3",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    fontWeight: 800,
                                                    fontSize: 12,
                                                    color: "#6b5c3f",
                                                    marginBottom: 5,
                                                }}
                                            >
                                                Claim note
                                            </div>

                                            <div
                                                style={{
                                                    fontSize: 13,
                                                    whiteSpace:
                                                        "pre-wrap",
                                                }}
                                            >
                                                {request.note ||
                                                    "No note provided."}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="cardFooter">
                                        <button
                                            type="button"
                                            className="btn-success"
                                            onClick={() =>
                                                approveClaim(
                                                    request
                                                )
                                            }
                                            disabled={disabled}
                                            style={{
                                                opacity: disabled
                                                    ? 0.7
                                                    : 1,
                                            }}
                                        >
                                            {disabled
                                                ? "Working…"
                                                : "Approve Claim"}
                                        </button>

                                        <button
                                            type="button"
                                            className="btn-danger"
                                            onClick={() =>
                                                rejectClaim(
                                                    request
                                                )
                                            }
                                            disabled={disabled}
                                            style={{
                                                opacity: disabled
                                                    ? 0.7
                                                    : 1,
                                            }}
                                        >
                                            {disabled
                                                ? "Working…"
                                                : "Reject Claim"}
                                        </button>
                                    </div>
                                </div>
                            );
                        }

                        const shopId =
                            request.shopId;

                        return (
                            <div
                                key={requestKey}
                                className="card"
                            >
                                <div className="cardHeader">
                                    <div>
                                        {showRequestBadges && (
                                            <div
                                                style={{
                                                    marginBottom: 9,
                                                }}
                                            >
                                                <RequestBadge type="registration" />
                                            </div>
                                        )}

                                        <h3
                                            className="cardTitle"
                                            style={{
                                                marginBottom: 2,
                                            }}
                                        >
                                            {request.name ||
                                                "(Unnamed shop)"}
                                            {request.town
                                                ? `, ${request.town}`
                                                : ""}
                                        </h3>

                                        <div className="cardSubtitle">
                                            <strong>
                                                Shop ID:
                                            </strong>{" "}
                                            {shopId}
                                        </div>
                                    </div>
                                </div>

                                <div className="cardBody">
                                    {request.email && (
                                        <p
                                            style={{
                                                margin: "6px 0",
                                            }}
                                        >
                                            <strong>
                                                Email:
                                            </strong>{" "}
                                            {request.email}
                                        </p>
                                    )}

                                    {request.address && (
                                        <p
                                            style={{
                                                margin: "6px 0",
                                            }}
                                        >
                                            <strong>
                                                Address:
                                            </strong>{" "}
                                            {request.address}
                                        </p>
                                    )}

                                    {request.contact && (
                                        <p
                                            style={{
                                                margin: "6px 0",
                                            }}
                                        >
                                            <strong>
                                                Contact:
                                            </strong>{" "}
                                            {request.contact}
                                        </p>
                                    )}
                                </div>

                                <div className="cardFooter">
                                    <button
                                        type="button"
                                        className="btn-success"
                                        onClick={() =>
                                            approveShop(shopId)
                                        }
                                        disabled={disabled}
                                        style={{
                                            opacity: disabled
                                                ? 0.7
                                                : 1,
                                        }}
                                    >
                                        {disabled
                                            ? "Working…"
                                            : "Approve"}
                                    </button>

                                    <button
                                        type="button"
                                        className="btn-danger"
                                        onClick={() =>
                                            rejectShop(shopId)
                                        }
                                        disabled={disabled}
                                        style={{
                                            opacity: disabled
                                                ? 0.7
                                                : 1,
                                        }}
                                    >
                                        {disabled
                                            ? "Working…"
                                            : "Reject"}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default ShopList;